/**
 * Casa Alcheme — Product Description Accordion
 * Converts h3-delimited sections in the product description into
 * tap-to-discover accordion panels. All sections start collapsed.
 *
 * Targets: the product description text block rendered by the Horizon theme.
 * The block carries class "text-block--text_aEtTtq rte" and the element is
 * a <rte-formatter> or <div> wrapping the product description HTML.
 *
 * Strategy:
 *   1. Find the description container inside .product-details
 *   2. Walk its direct children: anything before the first <h3> stays as-is
 *      (the evocative prose lead paragraph).
 *   3. For each <h3>, gather it plus all following siblings until the next
 *      <h3> into an accordion item.
 *   4. Replace those nodes with the accordion markup.
 *   5. Attach click handlers.
 *
 * Safe to run multiple times — guarded by data-accordion-applied attribute.
 */

(function () {
  'use strict';

  function buildAccordion(container) {
    // Guard: don't run twice
    if (container.hasAttribute('data-accordion-applied')) return;
    container.setAttribute('data-accordion-applied', 'true');

    // Find all h3 elements that are direct children (or grandchildren via rte-formatter)
    // We walk the childNodes of the container's inner content element.
    var contentRoot = container;

    // Some Horizon versions wrap content in a <div> or render as <rte-formatter>.
    // We look for h3 descendants at any depth, then find their common parent.
    var h3s = Array.prototype.slice.call(contentRoot.querySelectorAll('h3'));

    if (h3s.length === 0) {
      // No h3s found — nothing to accordion-ify
      return;
    }

    // Determine the parent of the h3s (they should share a common parent)
    var parent = h3s[0].parentNode;

    // Confirm all h3s share the same parent; if not, abort gracefully
    var allSameParent = h3s.every(function (h3) {
      return h3.parentNode === parent;
    });

    if (!allSameParent) {
      // Mixed nesting — don't risk mangling the DOM
      return;
    }

    // Collect all children of the parent node as a flat array
    var children = Array.prototype.slice.call(parent.childNodes);

    // Split into groups: [preamble, group1, group2, ...]
    // preamble = everything before the first h3
    // groupN = [h3_node, ...following siblings until next h3]
    var preambleNodes = [];
    var groups = [];
    var currentGroup = null;
    var firstH3Seen = false;

    children.forEach(function (node) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H3') {
        firstH3Seen = true;
        currentGroup = { heading: node.textContent.trim(), nodes: [node] };
        groups.push(currentGroup);
      } else if (!firstH3Seen) {
        preambleNodes.push(node);
      } else if (currentGroup) {
        currentGroup.nodes.push(node);
      }
    });

    if (groups.length === 0) return;

    // Build a document fragment with: preamble + accordion wrapper
    var fragment = document.createDocumentFragment();

    // Re-attach preamble nodes
    preambleNodes.forEach(function (node) {
      fragment.appendChild(node.cloneNode(true));
    });

    // Build the accordion wrapper
    var accordionWrapper = document.createElement('div');
    accordionWrapper.className = 'product-accordion';

    groups.forEach(function (group) {
      var item = document.createElement('div');
      item.className = 'product-accordion__item';

      // Trigger button
      var trigger = document.createElement('button');
      trigger.className = 'product-accordion__trigger';
      trigger.setAttribute('type', 'button');
      trigger.setAttribute('aria-expanded', 'false');

      var labelSpan = document.createElement('span');
      labelSpan.textContent = group.heading;

      var chevron = document.createElement('span');
      chevron.className = 'product-accordion__chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.textContent = '▸'; // ▸

      trigger.appendChild(labelSpan);
      trigger.appendChild(chevron);

      // Content panel
      var content = document.createElement('div');
      content.className = 'product-accordion__content';
      content.setAttribute('hidden', '');

      // Move all nodes from this group into the content panel
      // (clone to avoid re-parenting issues before we clear the DOM)
      group.nodes.forEach(function (node) {
        content.appendChild(node.cloneNode(true));
      });

      // Wire click handler
      trigger.addEventListener('click', function () {
        var isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          trigger.setAttribute('aria-expanded', 'false');
          content.setAttribute('hidden', '');
        } else {
          trigger.setAttribute('aria-expanded', 'true');
          content.removeAttribute('hidden');
        }
      });

      item.appendChild(trigger);
      item.appendChild(content);
      accordionWrapper.appendChild(item);
    });

    fragment.appendChild(accordionWrapper);

    // Clear the parent and append the rebuilt content
    while (parent.firstChild) {
      parent.removeChild(parent.firstChild);
    }
    parent.appendChild(fragment);
  }

  function init() {
    // Target: the description block inside the product details panel.
    // Horizon renders it with class "rte" and an id-based class "text-block--text_aEtTtq".
    // We use a broad selector so it works even if the block ID changes.
    // Primary selector: any element with both "rte" and "text-block" inside .product-details
    // that contains at least one h3.
    var candidates = document.querySelectorAll(
      '.product-details .rte, .product-details rte-formatter, .product__description'
    );

    var applied = false;
    candidates.forEach(function (el) {
      if (el.querySelector('h3') && !applied) {
        buildAccordion(el);
        applied = true;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
